import { useState } from 'react'
import { format } from 'date-fns'
import { ChevronDown, ChevronRight, Filter } from 'lucide-react'
import Badge from '@/components/admin/shared/Badge'
import type { AdminFeedbackEntry, FeedbackFilterConfig } from '@/api/adminService'

interface FeedbackTableProps {
  items: AdminFeedbackEntry[]
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

function getFilterSummary(fc: FeedbackFilterConfig | null) {
  if (!fc) return { hasFilters: false, jurisdictions: 0, pubAuthorities: 0, documents: 0 }
  const jurisdictions  = Array.isArray(fc.jurisdictions)    ? fc.jurisdictions.length    : 0
  const pubAuthorities = Array.isArray(fc.pub_authorities)  ? fc.pub_authorities.length  : 0
  const documents      = Array.isArray(fc.documents)        ? fc.documents.length        : 0
  return { hasFilters: jurisdictions > 0 || pubAuthorities > 0 || documents > 0, jurisdictions, pubAuthorities, documents }
}

export default function FeedbackTable({ items }: FeedbackTableProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null)

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <th className="w-8 px-4 py-3" />
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">Type</th>
            <th className="px-4 py-3">User Prompt</th>
            <th className="px-4 py-3">Active Filters</th>
            <th className="px-4 py-3">Comment</th>
            <th className="px-4 py-3">User</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((item) => {
            const isExpanded = expandedId === item.id
            const { hasFilters, jurisdictions, pubAuthorities, documents } = getFilterSummary(item.filter_config)
            return (
              <FeedbackRow
                key={item.id}
                item={item}
                isExpanded={isExpanded}
                hasFilters={hasFilters}
                jurisdictions={jurisdictions}
                pubAuthorities={pubAuthorities}
                documents={documents}
                onToggle={() => setExpandedId(isExpanded ? null : item.id)}
              />
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function FeedbackRow({
  item, isExpanded, hasFilters, jurisdictions, pubAuthorities, documents, onToggle,
}: {
  item: AdminFeedbackEntry
  isExpanded: boolean
  hasFilters: boolean
  jurisdictions: number
  pubAuthorities: number
  documents: number
  onToggle: () => void
}) {
  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={onToggle}>
        <td className="px-4 py-3 text-gray-400">
          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
          {format(new Date(item.created_at), 'MMM d, yyyy HH:mm')}
        </td>
        <td className="px-4 py-3"><Badge type={item.feedback_type} /></td>
        <td className="px-4 py-3 text-gray-700 max-w-xs">{truncate(item.user_prompt, 80)}</td>
        <td className="px-4 py-3">
          {hasFilters ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
              <Filter className="w-3 h-3" />
              {[
                jurisdictions  > 0 ? `${jurisdictions} jur.`   : '',
                pubAuthorities > 0 ? `${pubAuthorities} auth.` : '',
                documents      > 0 ? `${documents} doc.`       : '',
              ].filter(Boolean).join(', ')}
            </span>
          ) : (
            <span className="text-xs text-gray-300 italic">
              {item.filter_config === null ? 'Not captured' : 'None'}
            </span>
          )}
        </td>
        <td className="px-4 py-3 text-gray-500">
          {item.feedback_text || <span className="text-gray-300">-</span>}
        </td>
        <td className="px-4 py-3 text-gray-400 text-xs font-mono">{truncate(item.user_id, 12)}</td>
      </tr>

      {isExpanded && (
        <tr className="bg-gray-50/60">
          <td colSpan={7} className="px-4 py-4">
            <ExpandedDetail item={item} />
          </td>
        </tr>
      )}
    </>
  )
}

function ExpandedDetail({ item }: { item: AdminFeedbackEntry }) {
  const fc = item.filter_config
  const { hasFilters } = getFilterSummary(fc)

  return (
    <div className="space-y-4 ml-8 max-w-5xl">
      {fc && hasFilters && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Active Filters</h4>
          <div className="flex flex-wrap gap-4">
            {Array.isArray(fc.jurisdictions) && fc.jurisdictions.length > 0 && (
              <ChipGroup label="Jurisdictions" items={fc.jurisdictions.map(j => j?.name || j?.uuid || 'Unknown')} color="blue" />
            )}
            {Array.isArray(fc.pub_authorities) && fc.pub_authorities.length > 0 && (
              <ChipGroup label="Publishing Authorities" items={fc.pub_authorities.map(pa => pa?.name || pa?.uuid || 'Unknown')} color="purple" />
            )}
            {Array.isArray(fc.documents) && fc.documents.length > 0 && (
              <ChipGroup
                label="Documents"
                items={fc.documents.map(d => {
                  const title = d?.title || d?.legislation_uuid || 'Unknown'
                  const uuid  = d?.legislation_uuid
                  return uuid && title !== uuid ? `${title} (${uuid})` : title
                })}
                color="amber"
              />
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">User Prompt</h4>
          <div className="text-sm text-gray-700 bg-white border border-gray-200 rounded-md px-3 py-2 max-h-48 overflow-y-auto whitespace-pre-wrap">
            {item.user_prompt}
          </div>
        </div>
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Bot Response</h4>
          <div className="text-sm text-gray-700 bg-white border border-gray-200 rounded-md px-3 py-2 max-h-48 overflow-y-auto whitespace-pre-wrap">
            {item.bot_response}
          </div>
        </div>
      </div>

      <div className="flex gap-4 text-xs text-gray-400">
        <span>Conversation #{item.conversation_id}</span>
        <span>Message #{item.message_id}</span>
        <span>User: {item.user_id}</span>
      </div>
    </div>
  )
}

const chipColors = {
  blue:   'bg-blue-50 text-blue-700 border-blue-200',
  purple: 'bg-purple-50 text-purple-700 border-purple-200',
  amber:  'bg-amber-50 text-amber-700 border-amber-200',
} as const

function ChipGroup({ label, items, color }: { label: string; items: string[]; color: keyof typeof chipColors }) {
  return (
    <div>
      <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{label}</span>
      <div className="flex flex-wrap gap-1 mt-1">
        {items.map((item, i) => (
          <span key={i} className={`inline-block px-2 py-0.5 text-xs rounded border ${chipColors[color]}`}>
            {item}
          </span>
        ))}
      </div>
    </div>
  )
}
